import {
  Router,
  NavigationEnd,
  ActivatedRoute,
  ActivatedRouteSnapshot,
} from '@angular/router';
import {
  inject,
  Component,
  Renderer2,
  DestroyRef,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';

import { Highcharts } from './core/highcharts-shared';

import {
  Observable,
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  take,
  tap,
} from 'rxjs';
import {
  selectCoreDarkMode,
  selectCoreLanguage,
  selectHasShadow,
  selectIsAuthenticated,
  selectUser,
} from './core/reducers';
import {
  objectChanged,
  setAppSection,
  switchActiveModule,
} from './core/actions/core.actions';

import { AppState } from './reducers';
import { AuthService } from './core/services/auth.service';
import { BreadcrumbService } from './core/services/breadcrumb.service';
import { LastRouteService } from './core/services/last-route.service';
import { TextDirectionService } from './core/services/text-direction.service';
import { LoggerService } from '@services/logger.service';
import { MenuConfigService } from './core/services/menu-config.service';
import { Notification, User } from './core/models/core.models';
import { OnInit } from '@angular/core';
import { PageTitleService } from './core/services/page-title.service';
import { SocketService } from '@services/socket.service';
import { select, Store } from '@ngrx/store';
import { StoreService } from '@services/store.service';
import { Title } from '@angular/platform-browser';
import { ToastService } from '@services/toast.service';
// External modules
import { TranslateService } from '@ngx-translate/core';
import { UserAccessLevel } from './core/services/user.service';
import { QueryClient } from '@tanstack/angular-query-experimental';
import { prependUserNotification } from './api/queries/notification.queries';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

export interface UserNotificationMessage {
  type: string;
  notification: Notification;
}

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AppComponent implements OnInit {
  isAuthenticated$: Observable<boolean>;
  isPresentationMode$: Observable<boolean>;
  hasShadow$: Observable<boolean>;
  pageTitles$: Observable<string[]>;
  fullPageTitle: string;

  isBreadcrumbService = false;
  isPageTitleService = false;
  hideHeader = true; // TODO: maybe we need better naming
  bareLayout = false; // route opts out of the whole app shell (e.g. design-system)
  private previousAppSection: string | null = null;

  userAccessLevel: UserAccessLevel = UserAccessLevel.GUEST;
  UserAccessLevel = UserAccessLevel;

  protected destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);
  private queryClient = inject(QueryClient);

  constructor(
    private translateService: TranslateService,
    public storeService: StoreService,
    private store: Store<AppState>,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private breadcrumbService: BreadcrumbService,
    private renderer: Renderer2,
    private authService: AuthService,
    private pageTitleService: PageTitleService,
    private titleService: Title,
    private loggerService: LoggerService,
    private toastService: ToastService,
    private socketService: SocketService,
    private menuConfigService: MenuConfigService,
    private textDirectionService: TextDirectionService,
    _lastRouteService: LastRouteService, // injected for its side effect only: records the last visited route
  ) {
    this.translateService.setDefaultLang('en');
    let userLanguage = 'en';
    this.translateService.use(userLanguage);
    this.isAuthenticated$ = this.store.select(selectIsAuthenticated);

    this.hasShadow$ = this.store.select(selectHasShadow);
  }

  public ngOnInit(): void {
    this.store
      .pipe(
        select(selectCoreDarkMode),
        // only run side-effects when the flag actually changes
        distinctUntilChanged(),
        tap((darkMode) => {
          if (darkMode) {
            this.renderer.addClass(document.body, 'dark-mode');
            this.renderer.setAttribute(document.body, 'data-bs-theme', 'dark');
          } else {
            this.renderer.removeClass(document.body, 'dark-mode');
            this.renderer.setAttribute(document.body, 'data-bs-theme', 'light');
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.store
      .select(selectUser)
      .pipe(
        // only proceed when we have a user object
        filter((user): user is User => !!user),
        // take the first user emission then complete; avoids multiple connects
        take(1),
        // side-effect: establish socket connection with your callback
        tap((user) => {
          this.loggerService.debug('User connected', user);
          this.userAccessLevel = user.access_level_id;
          this.socketService.connectUserNotifications(
            user.id,
            (id, message: UserNotificationMessage) => {
              this.loggerService.debug('User notifications', message);

              if (message) {
                // State first: a toast rendering failure must never cost the notification.
                prependUserNotification(this.queryClient, message.notification);
                this.toastService.showCustomNotification(message.notification);
              }
            },
          );
        }),
        // auto-unsubscribe if the component is destroyed before the first emission
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.store
      .pipe(
        select(selectCoreLanguage),
        // only react when the language code or RTL flag actually changes
        distinctUntilChanged(
          (prev, curr) => prev.code === curr.code && prev.rtl === curr.rtl,
        ),
        // side-effects: set up translation, auth language, and body attributes
        tap((language) => {
          this.translateService.use(language.code);
          this.authService.setLanguage(language);

          if (language.rtl) {
            this.renderer.setAttribute(document.body, 'dir', 'rtl');
            this.renderer.addClass(document.body, 'rtl');
          } else {
            this.renderer.removeAttribute(document.body, 'dir');
            this.renderer.removeClass(document.body, 'rtl');
          }

          // * update text direction service for mixed-content handling
          this.textDirectionService.updateMode(language.rtl);
        }),
        // auto-unsubscribe when the component is destroyed
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    Highcharts.AST.allowedAttributes.push('dir');
    Highcharts.setOptions({
      global: {
        //timezoneOffset: -1 * 2 * 60,
      },
      lang: {
        numericSymbols: ['K', 'M', 'B', 'T', 'Q', null],
      },
      chart: {
        animation: true,
        style: {
          color: '#000',
        },
      },
      plotOptions: {
        series: {
          animation: true,
        },
      },
    });

    // * Initialize text direction service with initial language state
    this.store
      .pipe(
        select(selectCoreLanguage),
        take(1),
        tap((language) => {
          this.textDirectionService.initialize(language.rtl);
        }),
      )
      .subscribe();

    this.setupBreadcrumb();
    this.setupPageTitle();
    this.setupPresentationMode();
    this.setupBareLayout();

    this.pageTitleService.getRouterItems().subscribe((titles) => {
      const fullPageTitle = titles.join(' | ');
      this.titleService.setTitle(fullPageTitle);
    });
  }

  ngOnDestroy(): void {
    this.socketService.disconnectAll();
    this.textDirectionService.destroy();
  }

  setupPresentationMode() {
    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        take(1),
        tap(() => {
          const route = this.router.routerState.snapshot.root;
          this.hideHeader = this.getHideHeader(route);
        }),
      )
      .subscribe();
  }

  setupBareLayout() {
    const update = () => {
      this.bareLayout = this.isBareLayout(
        this.router.routerState.snapshot.root,
      );
      this.cdr.markForCheck();
    };

    update(); // initial (direct load)

    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(update);
  }

  setupBreadcrumb() {
    combineLatest([
      this.router.events.pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => this.activatedRoute),
      ),
      this.store.pipe(select(selectUser)),
    ])
      .pipe(
        // only proceed once we have a valid user
        filter(([, user]) => !!user),
        tap(([route, user]) => {
          // update the current app section
          this.updateAppSection();

          // refresh the breadcrumb for the new route & user
          this.breadcrumbService.updateBreadcrumb(route, user);

          // subscribe once for feed-state updates
          if (!this.isBreadcrumbService) {
            this.breadcrumbService.subscribeForFeedStateUpdates();
            this.isBreadcrumbService = true;
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  setupPageTitle() {
    combineLatest([
      this.router.events.pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => this.activatedRoute),
      ),
      this.store.pipe(select(selectUser)),
    ])
      .pipe(
        // only proceed once we have a valid user
        filter(([, user]) => !!user),
        tap(([route, user]) => {
          this.pageTitleService.updatePageTitle(route, user);

          if (!this.isPageTitleService) {
            this.pageTitleService.subscribeForFeedStateUpdates();
            this.isPageTitleService = true;
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  updateAppSection(): void {
    // Use optional chaining to safely access the section data
    const appSection = this.activatedRoute.snapshot.firstChild?.data['section'];
    if (!appSection) return;

    // Define a mapping from app section to module id
    const moduleMap: Record<string, number> = {
      analytics: 1,
      admin: 2,
      'source-catalog': 3,
      sysadmin: 4,
      'mobile-admin': 5,
    };

    // Get the module id from the map; default to 1 if section not found
    const moduleId = moduleMap[appSection] || 1;
    const menuItems = this.menuConfigService.getDefaultMenuItems(appSection);

    if (
      this.previousAppSection !== null &&
      this.previousAppSection !== appSection
    ) {
      this.store.dispatch(objectChanged({ object: null }));
    }
    this.previousAppSection = appSection;

    this.store.dispatch(setAppSection({ appSection, menuItems }));
    this.store.dispatch(switchActiveModule({ moduleId }));
  }

  private getHideHeader(route: ActivatedRouteSnapshot): boolean {
    let current = route;
    while (current.firstChild) current = current.firstChild;
    return current.data['hideHeader'] === true;
  }

  private isBareLayout(route: ActivatedRouteSnapshot): boolean {
    let current = route;
    while (current.firstChild) current = current.firstChild;
    return current.data['bareLayout'] === true;
  }
}
